import { useState, useEffect } from "react";
import type { TFile } from "../../models/file-model";
import type { TFolder } from "../../models/folder-model";
import type { TFilesRetriever } from "../../services/files-retriever";
import { TFolderService } from "../../services/draft-attachments-service";
import { useNotificationStore } from "../Notifications/notification-store";
import { AttachFilesPresentational, isFile, isFolder, TViewType } from "./AttachFilesPresentational";
import { logAttachFilesUsageMetric } from "../../helpers/MetricsHelper";
import { AttachFilesMetricActionConsts } from "../../helpers/MetricsHelper";

const searchNav = {
  open: true,
  active: false,
  id: "__search",
  subFolders: [],
  noDecoration: true,
  display: "View Search Results",
};

interface TProps {
  matterId: string;
  folderService: TFolderService;
  filesRetriever: TFilesRetriever;

  onCancel: () => void;
  onAttach: (files?: TFile[]) => void;
}

export interface IFolderSelectedFileCount {
  [key: string]: number;
}

export const AttachFilesContainer = ({ folderService, filesRetriever, matterId, onAttach, onCancel }: TProps) => {
  const [currentView, setCurrentView] = useState<TViewType>("FOLDER");

  const errorNotification = useNotificationStore((s) => s.errorNotification);

  // files the user has selected
  const [selected, setSelected] = useState<TFile[]>([]);

  // id of the current folder we should be displaying for
  const [currentFolderId, setCurrentFolderId] = useState("");

  // all files/folders for the current path
  const [files, setFiles] = useState<(TFile | TFolder)[] | null>(null);

  // holds the current file load progress state
  const [isLoadingFiles, setIsLoadingFiles] = useState<boolean>(false);

  // holds the current search progress state
  const [isSearching, setIsSearching] = useState<boolean>(false);

  // holds results from searches
  const [searchResults, setSearchResults] = useState<TFile[]>([]);

  // tracks if UI should show/hide email files (.msg / .eml)
  const [showEmails, setShowEmails] = useState(false);

  // tracks the count of each selected file in folders.
  const [folderToSelectedFilesCount, setFolderToSelectedFilesCount] = useState<IFolderSelectedFileCount>({});

  const searchFilesAsync = async (searchText: string): Promise<TFile[]> => {
    if (!searchText) {
      setIsSearching(false);
      const originalFiles = files?.filter((f) => isFile(f)) as TFile[];
      setCurrentView("FOLDER");
      return originalFiles;
    }

    setSearchResults([]);
    setIsSearching(true);
    setCurrentView("SEARCH");

    const { error, data } = await filesRetriever.searchForFiles(matterId, searchText);

    if (error) {
      setSearchResults([]);
      setIsSearching(false);

      errorNotification("Could not search files", error);

      return [];
    }

    setIsSearching(false);
    setSearchResults(data);

    await logAttachFilesUsageMetric(AttachFilesMetricActionConsts.search);

    return data;
  };

  // get currentFolder files/folders for list navigation
  useEffect(() => {
    setFiles([]);

    const loadData = async () => {
      let filesSkip = 0;
      let filesHasMore = true;
      const loadedFiles: typeof files = [];

      setIsLoadingFiles(true);

      while (filesHasMore) {
        const batch = await filesRetriever.getMatterFilesForFolder(matterId, {
          folderId: currentFolderId,
          skip: filesSkip,
        });

        if (batch.error) {
          filesHasMore = false;

          console.error("Error loading files: ", batch.error);
          errorNotification("Error loading files", batch.error);
        } else {
          const paging = batch.paging;

          if (paging && paging.taken && paging.hasMore) {
            filesHasMore = paging.hasMore;
            filesSkip = filesSkip + paging.taken;
          } else {
            filesHasMore = false;
          }

          loadedFiles.push(...(batch.data || []));
        }
      }

      // in-place sort yay js
      loadedFiles.sort((a, b) => {
        // same type so sort by alpha
        if (a.type === b.type) {
          return `${a.name}`.localeCompare(`${b.name}`);
        }
        // FOLDERs ordered before FILEs
        return a.type === "FOLDER" ? -1 : 1;
      });

      setFiles(loadedFiles);
      setIsLoadingFiles(false);
    };

    loadData().catch((e) => {
      setIsLoadingFiles(false);
      errorNotification("failed to load data", e);
    });
  }, [currentFolderId]);

  const shouldHideEmail = (f: TFile | TFolder) => {
    // don't hide folders
    if (isFolder(f)) {
      return false;
    }
    // if the user has selected the files, we should not hide them
    if (selected.find((sf) => sf.id === f.id)) {
      return false;
    }
    // hide known email extensions
    if (f.fileExtension === ".eml" || f.fileExtension === ".msg") {
      return true;
    }
    // all other files do not hide
    return false;
  };

  const onSelectFile = (target: TFile) => {
    const setFolderSelectedFileCount = (key: string, direction: "INCREMENT" | "DECREMENT"): void => {
      const getNewCount = (val: number | null | undefined): number => {
        if (!val) {
          val = 0;
        }

        if (direction === "DECREMENT") {
          if (val == 0) {
            return 0;
          }

          return val - 1;
        }

        return val + 1;
      };

      setFolderToSelectedFilesCount((current) => ({
        ...current,
        [key]: getNewCount(current[key]),
      }));
    };

    const unselect = !!selected.find((f) => f.id === target.id);
    if (unselect) {
      const newSelected = selected.filter((f) => f.id !== target.id);
      setSelected(newSelected);

      setFolderSelectedFileCount(target.folderId, "DECREMENT");
    } else {
      setSelected([...selected, target]);
      setFolderSelectedFileCount(target.folderId, "INCREMENT");
    }
  };

  const onSelectFolder = async (folderId: string) => {
    setCurrentFolderId(folderId);

    await logAttachFilesUsageMetric(AttachFilesMetricActionConsts.selectFolder);
  };

  const onClearSelected = async () => {
    setSelected([]);
    setFolderToSelectedFilesCount({});

    await logAttachFilesUsageMetric(AttachFilesMetricActionConsts.clearSelected);
  };

  const getFilesForView = () => {
    let sourceFiles = [] as TFile[];

    if (currentView === "SEARCH") {
      sourceFiles = searchResults;
    } else {
      sourceFiles = files?.filter((f) => isFile(f)) as TFile[];
    }

    return sourceFiles?.filter((f) => showEmails || !shouldHideEmail(f)) || null;
  };

  const onToggleShowEmails = async (toggleFn: (value: boolean) => boolean) => {
    const newShowEmailsValue = toggleFn(showEmails);
    setShowEmails(newShowEmailsValue);

    await logAttachFilesUsageMetric(
      newShowEmailsValue ? AttachFilesMetricActionConsts.showEmail : AttachFilesMetricActionConsts.hideEmail
    );
  };

  return (
    <AttachFilesPresentational
      matterId={matterId}
      selected={selected}
      viewType={currentView}
      showEmails={showEmails}
      isSearching={isSearching}
      files={getFilesForView()}
      emptyMessage="No files..."
      folderService={folderService}
      searchInProgress={isSearching}
      filesRetriever={filesRetriever}
      fileLoadInProgress={isLoadingFiles}
      folderAndSelectedFileCounts={folderToSelectedFilesCount}
      onCancel={onCancel}
      onSelectFile={onSelectFile}
      onSelectFolder={onSelectFolder}
      clearSelected={onClearSelected}
      onAttach={() => onAttach(selected)}
      searchForFilesAsync={searchFilesAsync}
      toggleShowEmails={() => onToggleShowEmails((v) => !v)}
    />
  );
};
